import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message } from 'antd';
import { Company } from './CompanyAutocomplete';
import { GridLayout } from '../contract/GridLayout';
import { SectionHeader } from '../contract/SectionHeader';
import { colors, spacing } from '../design-system/tokens';
import { suppliersApi, buyersApi, sellersApi } from '../../services/api';

const { Option } = Select;

interface CompanyModalProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (company: Company) => void;
  companyType: 'supplier' | 'buyer' | 'seller';
  editingCompany?: Company;
}

const countries = [
  'United States', 'Japan', 'Canada', 'Mexico', 'United Kingdom', 
  'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Other'
];

const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const CompanyModal: React.FC<CompanyModalProps> = ({
  visible,
  onCancel,
  onSave,
  companyType,
  editingCompany
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>('United States');

  useEffect(() => {
    if (visible) {
      if (editingCompany) {
        if (companyType === 'seller') {
          // For sellers, address is a single string
          form.setFieldsValue({
            name: editingCompany.name,
            shortCode: editingCompany.shortCode,
            address: editingCompany.address.street // Full address string
          });
        } else {
          form.setFieldsValue({
            name: editingCompany.name,
            shortCode: editingCompany.shortCode,
            street: editingCompany.address.street,
            city: editingCompany.address.city,
            state: editingCompany.address.state,
            postalCode: editingCompany.address.postalCode,
            country: editingCompany.address.country
          });
          setSelectedCountry(editingCompany.address.country);
        }
      } else {
        form.resetFields();
        if (companyType !== 'seller') {
          form.setFieldsValue({
            country: 'United States'
          });
          setSelectedCountry('United States');
        }
      }
    }
  }, [visible, editingCompany, form, companyType]);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      let savedCompany: any;

      if (companyType === 'seller') {
        // Sellers use abbreviation instead of code and have single address field
        const sellerData = {
          abbreviation: values.shortCode,
          name: values.name,
          address: values.address || '',
          active: true
        };

        if (editingCompany && editingCompany.id !== `new-${Date.now()}`) {
          savedCompany = await sellersApi.update(editingCompany.id, sellerData);
        } else {
          savedCompany = await sellersApi.create(sellerData);
        }
      } else if (companyType === 'supplier') {
        const supplierData = {
          code: values.shortCode,
          name: values.name,
          address: [values.street, values.city, values.state, values.postalCode].filter(Boolean).join(', '),
          country: values.country,
          active: true
        };

        if (editingCompany && editingCompany.id !== `new-${Date.now()}`) {
          savedCompany = await suppliersApi.update(editingCompany.id, supplierData);
        } else {
          savedCompany = await suppliersApi.create(supplierData);
        }
      } else {
        // Buyer
        const buyerData = {
          code: values.shortCode,
          name: values.name,
          address: [values.street, values.city, values.state, values.postalCode].filter(Boolean).join(', '),
          country: values.country,
          active: true
        };

        if (editingCompany && editingCompany.id !== `new-${Date.now()}`) {
          savedCompany = await buyersApi.update(editingCompany.id, buyerData);
        } else {
          savedCompany = await buyersApi.create(buyerData);
        }
      }

      // Build Company object for callback
      const newCompany: Company = {
        id: savedCompany?.id || editingCompany?.id || `new-${Date.now()}`,
        name: values.name,
        code: values.shortCode,
        shortCode: values.shortCode,
        address: companyType === 'seller'
          ? { street: values.address || '', city: '', state: '', postalCode: '', country: '' }
          : { street: values.street, city: values.city, state: values.state, postalCode: values.postalCode, country: values.country },
        type: companyType
      };

      onSave(newCompany);
      message.success(`${companyType} ${editingCompany ? 'updated' : 'created'} successfully!`);

    } catch (error) {
      console.error('Failed to save company:', error);
      message.error(`Failed to ${editingCompany ? 'update' : 'create'} ${companyType}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    if (country !== 'United States') {
      form.setFieldsValue({ state: undefined });
    }
  };

  const isUsAddress = selectedCountry === 'United States';
  const modalTitle = editingCompany 
    ? `Edit ${companyType.charAt(0).toUpperCase() + companyType.slice(1)}`
    : `Create New ${companyType.charAt(0).toUpperCase() + companyType.slice(1)}`;

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: spacing.lg }}
      >
        <SectionHeader title="Company Information" />
        
        <GridLayout columns="3fr 1fr" style={{ marginTop: spacing.lg }}>
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: 'Company name is required' }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>

          <Form.Item
            name="shortCode"
            label={`Short Code (${companyType === 'supplier' || companyType === 'seller' ? '2' : '3'} letters)`}
            rules={[
              { required: true, message: 'Short code is required' },
              { 
                pattern: companyType === 'supplier' || companyType === 'seller' ? /^[A-Z]{2}$/ : /^[A-Z]{3}$/,
                message: `Must be exactly ${companyType === 'supplier' || companyType === 'seller' ? '2' : '3'} uppercase letters`
              }
            ]}
            normalize={(value) => value?.toUpperCase()}
          >
            <Input 
              placeholder={companyType === 'supplier' || companyType === 'seller' ? 'AN' : 'ANA'} 
              maxLength={companyType === 'supplier' || companyType === 'seller' ? 2 : 3}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>
        </GridLayout>

        <SectionHeader
          title="Address Information"
          style={{ marginTop: spacing.xl }}
        />

        {companyType === 'seller' ? (
          /* Sellers have a single address field */
          <Form.Item
            name="address"
            label="Full Address"
            style={{ marginTop: spacing.lg }}
          >
            <Input.TextArea
              placeholder="Enter full address (e.g., 123 Main St, City, State 12345, Country)"
              rows={3}
            />
          </Form.Item>
        ) : (
          /* Suppliers and Buyers have structured address fields */
          <>
            <GridLayout columns="2fr 1fr" style={{ marginTop: spacing.lg }}>
              <Form.Item
                name="street"
                label="Street Address"
                rules={[{ required: true, message: 'Street address is required' }]}
              >
                <Input placeholder="Enter street address" />
              </Form.Item>

              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'City is required' }]}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </GridLayout>

            <GridLayout columns="1fr 1fr 2fr" style={{ marginTop: spacing.md }}>
              {isUsAddress && (
                <Form.Item
                  name="state"
                  label="State"
                >
                  <Select placeholder="Select state">
                    {usStates.map(state => (
                      <Option key={state} value={state}>{state}</Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                name="postalCode"
                label="Postal Code"
                rules={[{ required: true, message: 'Postal code is required' }]}
                style={!isUsAddress ? { gridColumn: 'span 1' } : {}}
              >
                <Input placeholder="Enter postal code" />
              </Form.Item>

              <Form.Item
                name="country"
                label="Country"
                rules={[{ required: true, message: 'Country is required' }]}
                initialValue="United States"
              >
                <Select
                  placeholder="Select country"
                  onChange={handleCountryChange}
                >
                  {countries.map(country => (
                    <Option key={country} value={country}>{country}</Option>
                  ))}
                </Select>
              </Form.Item>
            </GridLayout>
          </>
        )}

        {/* Form Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: spacing.md,
          marginTop: spacing.xl,
          paddingTop: spacing.lg,
          borderTop: `1px solid ${colors.border.default}`
        }}>
          <Button onClick={onCancel} size="large">
            Cancel
          </Button>
          
          <Button 
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            size="large"
          >
            {editingCompany ? 'Update' : 'Create'} {companyType.charAt(0).toUpperCase() + companyType.slice(1)}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};